import { CtaPill } from './primitives/CtaPill';
import { Section } from './primitives/Section';
import { ArchCard } from './primitives/ArchCard';
import { AppPreview } from './primitives/AppPreview';
import { MonoKicker } from './primitives/MonoKicker';

/**
 * The 1.1 / 1 split uses `flex-basis: 0%` with grow ratios rather than a grid,
 * because `auto-fit` cannot express a ratio and a viewport breakpoint would
 * fire at the wrong moment — the section is `max-w-[1400px]` inside clamped
 * gutters, so the width that decides whether two columns fit is the content
 * box, not the viewport. With a zero basis all free space is distributed by
 * grow, so the split is exactly 1.1 : 1 rather than 1.1 : 1 of the remainder.
 *
 * The min-width uses `min(Npx, 100%)` rather than a bare pixel value, which is
 * what makes the columns wrap instead of overflow — 460px alone is wider than
 * the content box of a 375px phone.
 */
export function Hero() {
    return (
        <Section id="top" className="pt-[clamp(40px,6vw,72px)]">
            <div className="flex flex-wrap items-center gap-[clamp(28px,4vw,64px)]">
                <div className="flex min-w-[min(460px,100%)] flex-[1.1_1_0%] flex-col gap-8">
                    <div className="flex flex-col gap-6">
                        <MonoKicker data-reveal>
                            An AI team for your practice
                        </MonoKicker>

                        <h1
                            data-reveal
                            className="font-display text-[clamp(44px,6.2vw,64px)] leading-[1.05] tracking-[-0.02em] text-balance"
                        >
                            You don&rsquo;t need another app. You need a team.
                        </h1>

                        <p
                            data-reveal
                            className="max-w-[54ch] text-[clamp(16px,1.4vw,18px)] leading-[1.55] text-muted-foreground"
                        >
                            MohairNotes is an AI clinical team you bring on, not
                            a subscription you figure out alone. We start with
                            an ambient scribe that drafts your notes and files
                            them into a real client chart — then keep going,
                            wiring AI into your calendar, your CRM, and whatever
                            else is quietly costing your practice its evenings.
                        </p>
                    </div>

                    <div
                        data-reveal
                        className="flex flex-wrap items-center gap-4"
                    >
                        <CtaPill href="/login">
                            Book a working session
                        </CtaPill>
                        <a
                            href="#what"
                            className="inline-flex items-center justify-center rounded-full border-[1.5px] border-foreground bg-transparent px-7 py-4 font-mono text-[12px] tracking-[0.08em] text-foreground uppercase transition-colors duration-200 hover:bg-foreground hover:text-background"
                        >
                            See what we build
                        </a>
                    </div>

                    <p className="font-mono text-[11px] tracking-[0.04em] text-muted-foreground">
                        30 minutes with the people who&rsquo;d do the work. Not
                        a sales engineer.
                    </p>
                </div>

                <div className="flex min-w-[min(400px,100%)] flex-[1_1_0%] justify-center">
                    <ArchCard
                        data-reveal
                        shape="dome"
                        tone="oxblood"
                        className="flex aspect-[4/3] w-full max-w-[600px] flex-col justify-between gap-6 p-[clamp(20px,3vw,32px)]"
                    >
                        <AppPreview />

                        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 font-mono text-[11px] tracking-[0.06em] text-muted-foreground">
                            <span>build 04 · calendar sync</span>
                            <span className="flex items-center gap-2">
                                <span
                                    aria-hidden="true"
                                    className="size-2 rounded-full bg-brand-sage"
                                />
                                shipped tuesday
                            </span>
                        </div>
                    </ArchCard>
                </div>
            </div>
        </Section>
    );
}
